/**
 * Bottom footer section for the task conversation panel.
 *
 * @module tasksConversationFooter
 */
import { useState } from "react";
import TaskSendMessageIcon from "../../../../../svg/taskSendMessageIcon";
import TaskCompleteConfirmModal, {
  type TaskCompleteConfirmVariant,
} from "./taskCompleteConfirmModal";

/*
/** Selected message recipient stored by OncoEMR user id. * /
interface SelectedRecipient {
  oncoemrUserId: string;
  displayName: string;
}
*/

/**
 * Props for {@link TasksConversationFooter}.
 */
interface TasksConversationFooterProps {
  placeholder?: string;
  onSendMessage?: (body: string) => Promise<void>;
  isSendingMessage?: boolean;
}

/**
 * Renders footer controls for task conversations.
 *
 * @param props - Optional placeholder override for the message input.
 * @returns Footer composer UI for task conversations.
 */
const TasksConversationFooter = ({
  placeholder = "Write your message",
  onSendMessage,
  isSendingMessage = false,
}: TasksConversationFooterProps) => {
  // const { recipientOptions, loading: usersLoading } = useOncoemrUsers();
  const [messageDraft, setMessageDraft] = useState("");
  const [completeConfirmVariant, setCompleteConfirmVariant] =
    useState<TaskCompleteConfirmVariant | null>(null);
  /*
  const [selectedRecipients, setSelectedRecipients] = useState<
    SelectedRecipient[]
  >([]);
  const [tagsResetKey, setTagsResetKey] = useState(0);

  const recipientTagLabels = useMemo(
    () => selectedRecipients.map((recipient) => recipient.displayName),
    [selectedRecipients],
  );

  const availableRecipientOptions = useMemo(
    () =>
      recipientOptions
        .filter(
          (option) =>
            !selectedRecipients.some(
              (recipient) => recipient.oncoemrUserId === option.value,
            ),
        )
        .map((option) => ({
          ...option,
          backgroundColor: "#F5F5F5",
          textColor: "#0F0F0F",
        })),
    [recipientOptions, selectedRecipients],
  );

  /**
   * Opens the correct complete / send-and-complete confirmation based on whether
   * the composer has non-whitespace text.
   * /
  const handleSendAndCompleteClick = () => {
    const hasMessage = messageDraft.trim().length > 0;
    setCompleteConfirmVariant(
      hasMessage ? "sendAndComplete" : "markCompleteOnly",
    );
  };
  */

  /**
   * Confirms complete (and send when applicable); clears draft only after success.
   * Rejects when {@link onSendMessage} fails so the confirm modal stays open.
   */
  const handleCompleteConfirmed = async (): Promise<void> => {
    const draft = messageDraft.trim();
    if (
      completeConfirmVariant === "sendAndComplete" &&
      draft &&
      onSendMessage
    ) {
      await onSendMessage(draft);
    }
    // TODO: call complete-only API when integrated.
    setMessageDraft("");
  };

  /**
   * Queues the composer draft as an outbound reply when non-empty.
   */
  const handleSendMessageClick = async () => {
    const draft = messageDraft.trim();
    if (!draft || !onSendMessage || isSendingMessage) {
      return;
    }

    try {
      await onSendMessage(draft);
      setMessageDraft("");
    } catch {
      // Parent surfaces toast; keep draft for retry.
    }
  };

  /*
  /**
   * Removes a recipient chip when the user closes a tag in the filter bar.
   *
   * @param label - Display label of the tag to remove.
   * /
  const handleRecipientTagRemove = (label: string) => {
    setSelectedRecipients((prev) =>
      prev.filter((recipient) => recipient.displayName !== label),
    );
  };

  /**
   * Appends a recipient from the picker and resets the inline select.
   *
   * @param option - Selected user option (`value` is `oncoemr_user_id`, `name` is display label).
   * /
  const handleRecipientOptionChange = (option: {
    value: string;
    label?: string;
    name?: string;
  }) => {
    if (!option || !option.value) {
      return;
    }

    const displayName = option.label ?? option.name ?? option.value;

    setSelectedRecipients((prev) => {
      if (prev.some((recipient) => recipient.oncoemrUserId === option.value)) {
        return prev;
      }
      return [
        ...prev,
        {
          oncoemrUserId: option.value,
          displayName,
        },
      ];
    });
  };
  */

  return (
    <footer className="shrink-0 border-t border-primaryGray-14 bg-white px-3 py-2">
      {/* <div className="mb-2 flex min-w-0 flex-wrap items-center gap-2">
        {recipientTagLabels.length > 0 && (
          <div className="min-w-0">
            <TagFilterBar
              tags={recipientTagLabels}
              closable
              showDropdown={false}
              singleLineScrollable
              className="tasks-conversation-footer__recipient-tags max-w-full"
              onTagRemove={handleRecipientTagRemove}
            />
          </div>
        )}
        <div className="shrink-0">
          <Tags
            key={tagsResetKey}
            label={"+ Add Recipients"}
            options={availableRecipientOptions}
            onSelect={(option) => {
              handleRecipientOptionChange({
                value: option.value,
                label: option.label,
              });
              // reset the inline select so the placeholder reappears
              setTagsResetKey((k) => k + 1);
            }}
            className="text-tiny font-semibold leading-[1.125rem] text-[#0056D6]"
            backgroundColor="#F5F5F5"
            textColor="#0F0F0F"
          />
        </div>
      </div> */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={messageDraft}
          onChange={(event) => setMessageDraft(event.target.value)}
          placeholder={placeholder}
          className="h-10 flex-1 rounded border border-primaryGray-14 px-3 text-[0.75rem] font-normal leading-[1.125rem] tracking-[0.0125rem] text-primaryGray-2 outline-none placeholder:text-primaryGray-7"
        />
        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center rounded bg-primaryGray-1 text-white disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Send message"
          disabled={
            isSendingMessage ||
            messageDraft.trim().length === 0 ||
            !onSendMessage
          }
          onClick={() => void handleSendMessageClick()}
        >
          <TaskSendMessageIcon className="h-5 w-5 shrink-0" />
        </button>
        {/* <button
          type="button"
          className="flex h-10 w-10 items-center justify-center rounded border border-primaryGray-12 text-primaryGray-8"
          aria-label="Send and mark task complete"
          onClick={handleSendAndCompleteClick}
        >
          <TaskActionCheckIcon className="aspect-square h-5 w-5 shrink-0" />
        </button> */}
      </div>
      {completeConfirmVariant !== null && (
        <TaskCompleteConfirmModal
          variant={completeConfirmVariant}
          onClose={() => setCompleteConfirmVariant(null)}
          onConfirm={handleCompleteConfirmed}
        />
      )}
    </footer>
  );
};

export default TasksConversationFooter;
